import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  X,
  Search,
  Package,
  Check,
  Loader2,
  AlertTriangle,
  Image as ImageIcon,
  MapPin,
  Tag,
  Palette,
  Ruler,
  CheckSquare,
  Square,
  Download,
  Filter,
  Zap,
  Hash,
  Info,
  DollarSign,
} from 'lucide-react';
import { Button } from '../ui/Button';

export type LocationQuantity = {
  shopifyLocationId: string;
  locationName: string;
  quantity: number;
};

export type AmbiguousMatch = {
  sku: string;
  shopify_variant_id: string;
  reason: 'duplicate_local_products' | 'duplicate_shopify_sku';
  matched_by: 'variant_id' | 'sku';
  product_ids: string[];
};

export type PreviewProduct = {
  sku: string;
  name: string;
  barcode: string | null;
  category: string;
  color_name: string | null;
  color_hex: string | null;
  size_name: string | null;
  quantity: number;
  location: string | null;
  locations?: LocationQuantity[];
  image_url: string | null;
  supplier: string | null;
  description: string | null;
  shopify_product_id: string;
  shopify_variant_id: string;
  inventory_item_id?: string | null;
  sku_auto_generated?: boolean;
  original_sku?: string | null;
  unit_price?: number;
  cost_price?: number;
  compare_at_price?: number;
};

interface ImportSettings {
  default_category: string;
  default_location: string;
  default_min_stock: number;
}

interface LoadingProgress {
  phase: 'products' | 'inventory' | 'complete';
  loaded: number;
  total: number;
}

interface ImportPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: PreviewProduct[];
  existingCategories: string[];
  existingLocations: string[];
  onImport: (selectedProducts: PreviewProduct[], settings: ImportSettings) => Promise<void>;
  loading?: boolean;
  loadingProgress?: LoadingProgress | null;
  stats?: {
    total: number;
    existing: number;
    with_sku: number;
    without_sku: number;
    draft_skipped: number;
    /** Counted inside `existing`; needs duplicate cleanup before it can import. */
    ambiguous: number;
  } | null;
  ambiguousMatches?: AmbiguousMatch[];
}

export const ImportPreviewModal: React.FC<ImportPreviewModalProps> = ({
  isOpen,
  onClose,
  products,
  existingCategories,
  existingLocations,
  onImport,
  loading = false,
  loadingProgress,
  stats,
  ambiguousMatches = [],
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [skuFilter, setSkuFilter] = useState<'all' | 'with' | 'without'>('all');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  // `ambiguousMatches` is a capped sample, so the headline uses the exact count
  // from the preview and falls back to the sample only on an older deployment
  // that does not return it.
  const ambiguousTotal = stats?.ambiguous || ambiguousMatches.length;
  const mergeCount = ambiguousMatches.filter(
    m => m.reason === 'duplicate_local_products'
  ).length;
  const renameCount = ambiguousMatches.filter(
    m => m.reason === 'duplicate_shopify_sku'
  ).length;

  useEffect(() => {
    if (products.length > 0) {
      setSelectedSkus(new Set(products.map(p => p.sku)));
    }
  }, [products]);

  const [settings, setSettings] = useState<ImportSettings>({
    default_category: 'Shopify Import',
    default_location: '',
    default_min_stock: 5,
  });

  const categories = useMemo(() => {
    const cats = new Set<string>();
    products.forEach(p => {
      if (p.category) cats.add(p.category);
    });
    return Array.from(cats).sort();
  }, [products]);

  const isWithoutSku = useCallback(
    (p: PreviewProduct) => p.sku_auto_generated === true || p.sku.startsWith('SHOP-'),
    []
  );

  const skuCounts = useMemo(() => {
    let without = 0;
    products.forEach(p => {
      if (isWithoutSku(p)) without += 1;
    });
    return { all: products.length, without, with: products.length - without };
  }, [products, isWithoutSku]);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesSearch = !searchTerm ||
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.barcode && p.barcode.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCategory = !categoryFilter || p.category === categoryFilter;

      const matchesSku =
        skuFilter === 'all' ||
        (skuFilter === 'without' ? isWithoutSku(p) : !isWithoutSku(p));

      return matchesSearch && matchesCategory && matchesSku;
    });
  }, [products, searchTerm, categoryFilter, skuFilter, isWithoutSku]);

  const handleToggleAll = () => {
    if (selectedSkus.size === filteredProducts.length) {
      const newSelected = new Set(selectedSkus);
      filteredProducts.forEach(p => newSelected.delete(p.sku));
      setSelectedSkus(newSelected);
    } else {
      const newSelected = new Set(selectedSkus);
      filteredProducts.forEach(p => newSelected.add(p.sku));
      setSelectedSkus(newSelected);
    }
  };

  const handleToggleProduct = (sku: string) => {
    const newSelected = new Set(selectedSkus);
    if (newSelected.has(sku)) {
      newSelected.delete(sku);
    } else {
      newSelected.add(sku);
    }
    setSelectedSkus(newSelected);
  };

  const handleImport = async () => {
    const selectedProducts = products.filter(p => selectedSkus.has(p.sku));
    if (selectedProducts.length === 0) return;

    setImporting(true);
    setImportError(null);
    try {
      await onImport(selectedProducts, settings);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Failed to import products');
    } finally {
      setImporting(false);
    }
  };

  const handleImageError = useCallback((sku: string) => {
    setFailedImages(prev => {
      const next = new Set(prev);
      next.add(sku);
      return next;
    });
  }, []);

  const allFilteredSelected = filteredProducts.length > 0 &&
    filteredProducts.every(p => selectedSkus.has(p.sku));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute inset-4 sm:inset-8 lg:inset-12 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-600 to-cyan-600">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Package className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                Import Products from Shopify
              </h2>
              <div className="flex items-center gap-4 text-sm text-white/90">
                <span>{products.length} new to import</span>
                {stats && (
                  <>
                    {stats.existing > 0 && (
                      <span className="flex items-center gap-1">
                        <Check className="h-3 w-3" />
                        {stats.existing} existing
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Hash className="h-3 w-3" />
                      {stats.with_sku} with SKU
                    </span>
                    {stats.without_sku > 0 && (
                      <span className="flex items-center gap-1 bg-amber-500/30 px-2 py-0.5 rounded">
                        <Zap className="h-3 w-3" />
                        {stats.without_sku} auto-SKU
                      </span>
                    )}
                    {stats.draft_skipped > 0 && (
                      <span className="opacity-75">{stats.draft_skipped} drafts skipped</span>
                    )}
                    {stats.ambiguous > 0 && (
                      <span
                        className="flex items-center gap-1 bg-red-500/30 px-2 py-0.5 rounded"
                        title="Included in the existing count above, not additional to it"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        {stats.ambiguous} of those need cleanup
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden">
            {ambiguousMatches.length > 0 && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-red-700 dark:text-red-300">
                      {ambiguousTotal} variant{ambiguousTotal === 1 ? '' : 's'} could not be matched
                      to a single product
                    </p>
                    <p className="text-xs text-red-600/80 dark:text-red-400/80 mt-0.5">
                      {/* Counted as existing above, so the numbers do not sum. */}
                      {'These are counted as existing and left out of the import, so no further duplicates are created. '}
                      {mergeCount > 0 &&
                        `Merge the ${mergeCount} duplicated product${
                          mergeCount === 1 ? '' : 's'
                        } in Inventory. `}
                      {renameCount > 0 &&
                        `Rename ${renameCount} clashing SKU${
                          renameCount === 1 ? '' : 's'
                        } in Shopify. `}
                      Then preview again.
                    </p>
                    <ul className="mt-2 max-h-28 overflow-y-auto space-y-1">
                      {ambiguousMatches.map(match => (
                        <li
                          key={`${match.shopify_variant_id}-${match.sku}`}
                          className="text-xs text-red-700 dark:text-red-300"
                        >
                          <span className="font-medium">{match.sku}</span>
                          {' — '}
                          {match.reason === 'duplicate_shopify_sku'
                            ? 'another Shopify variant already claims this SKU; rename one in Shopify'
                            : `matches ${match.product_ids.length} products by ${
                                match.matched_by === 'variant_id' ? 'variant id' : 'SKU'
                              }; merge them`}
                        </li>
                      ))}
                    </ul>
                    {ambiguousTotal > ambiguousMatches.length && (
                      <p className="text-xs text-red-600/80 dark:text-red-400/80 mt-1">
                        Showing the first {ambiguousMatches.length} of {ambiguousTotal}.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[240px] relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, SKU, or barcode..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-100 dark:bg-gray-800 border-0 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <select
                    value={categoryFilter}
                    onChange={e => setCategoryFilter(e.target.value)}
                    className="px-3 py-2.5 bg-gray-100 dark:bg-gray-800 border-0 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                  {([
                    { key: 'all', label: `All (${skuCounts.all})` },
                    { key: 'with', label: `With SKU (${skuCounts.with})` },
                    { key: 'without', label: `No SKU (${skuCounts.without})` },
                  ] as const).map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setSkuFilter(opt.key)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        skuFilter === opt.key
                          ? 'bg-white dark:bg-gray-700 text-cyan-600 dark:text-cyan-400 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <button
                  onClick={handleToggleAll}
                  className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors"
                >
                  {allFilteredSelected ? (
                    <CheckSquare className="h-4 w-4" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                  {allFilteredSelected ? 'Deselect All' : 'Select All'} ({filteredProducts.length})
                </button>

                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <span className="font-medium text-cyan-600">{selectedSkus.size}</span> selected
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-auto">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center max-w-md px-6">
                    <Loader2 className="h-10 w-10 animate-spin text-cyan-600 mx-auto mb-4" />
                    {loadingProgress ? (
                      <>
                        <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                          {loadingProgress.phase === 'products' && 'Loading products...'}
                          {loadingProgress.phase === 'inventory' && 'Loading inventory levels...'}
                          {loadingProgress.phase === 'complete' && 'Finalizing...'}
                        </p>
                        <div className="mb-3">
                          <div className="flex items-center justify-center gap-2 text-2xl font-bold text-cyan-600">
                            <span>{loadingProgress.loaded.toLocaleString()}</span>
                            <span className="text-gray-400 font-normal">/</span>
                            <span className="text-gray-500 dark:text-gray-400">{loadingProgress.total.toLocaleString()}</span>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {loadingProgress.phase === 'products' ? 'products' : 'inventory items'}
                          </p>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2.5 rounded-full transition-all duration-300"
                            style={{
                              width: `${loadingProgress.total > 0 ? Math.min(100, (loadingProgress.loaded / loadingProgress.total) * 100) : 0}%`
                            }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                          {loadingProgress.phase === 'products'
                            ? 'Scanning your Shopify catalog...'
                            : loadingProgress.phase === 'inventory'
                              ? 'Fetching stock levels from all locations...'
                              : 'Almost done...'}
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400">Loading products from Shopify...</p>
                    )}
                  </div>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center max-w-md">
                    <Package className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 mb-2">No new products to import</p>
                    {stats && stats.existing > 0 && products.length === 0 && (
                      <p className="text-sm text-gray-400 dark:text-gray-500">
                        All {stats.existing} products from Shopify already exist in your inventory.
                        Use "Sync Existing" to update quantities and details.
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800/50 sticky top-0">
                    <tr>
                      <th className="w-12 px-4 py-3"></th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        SKU
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Barcode
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Color
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Size
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Location
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {filteredProducts.map((product, idx) => (
                      <tr
                        key={`${product.sku}-${product.shopify_variant_id || idx}`}
                        onClick={() => handleToggleProduct(product.sku)}
                        className={`cursor-pointer transition-colors ${
                          selectedSkus.has(product.sku)
                            ? 'bg-cyan-50 dark:bg-cyan-900/20'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                        }`}
                      >
                        <td className="px-4 py-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            selectedSkus.has(product.sku)
                              ? 'bg-cyan-600 border-cyan-600'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}>
                            {selectedSkus.has(product.sku) && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                              {product.image_url && !failedImages.has(product.sku) ? (
                                <img
                                  src={product.image_url}
                                  alt={product.name}
                                  crossOrigin="anonymous"
                                  className="w-full h-full object-cover"
                                  onError={() => handleImageError(product.sku)}
                                />
                              ) : (
                                <ImageIcon className="h-5 w-5 text-gray-400" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[200px]">
                                {product.name}
                              </p>
                              {product.supplier && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {product.supplier}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {product.sku_auto_generated && (
                              <span
                                className="inline-flex items-center justify-center w-4 h-4 bg-amber-100 dark:bg-amber-900/30 rounded"
                                title="Auto-generated SKU (no SKU in Shopify)"
                              >
                                <Zap className="h-2.5 w-2.5 text-amber-600 dark:text-amber-400" />
                              </span>
                            )}
                            <code className={`text-xs px-2 py-1 rounded ${
                              product.sku_auto_generated
                                ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                                : 'bg-gray-100 dark:bg-gray-800'
                            }`}>
                              {product.sku}
                            </code>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {product.barcode ? (
                            <code className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                              {product.barcode}
                            </code>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {product.color_name ? (
                            <div className="flex items-center gap-2">
                              <div
                                className="w-4 h-4 rounded-full border border-gray-200 dark:border-gray-600"
                                style={{ backgroundColor: product.color_hex || '#ccc' }}
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {product.color_name}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {product.size_name ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                              {product.size_name}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                            {product.category || 'Uncategorized'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-medium ${
                            product.quantity === 0
                              ? 'text-red-600'
                              : product.quantity <= 10
                                ? 'text-amber-600'
                                : 'text-gray-900 dark:text-gray-100'
                          }`}>
                            {product.quantity}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {product.unit_price !== undefined && product.unit_price > 0 ? (
                            <div>
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(product.unit_price)}
                              </span>
                              {product.cost_price !== undefined && product.cost_price > 0 && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Cost: {new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(product.cost_price)}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {product.locations && product.locations.length > 0 ? (
                            <div className="flex flex-col gap-0.5 text-sm text-gray-600 dark:text-gray-400">
                              {product.locations.length === 1 ? (
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3 flex-shrink-0" />
                                  {product.locations[0].locationName}: {product.locations[0].quantity}
                                </div>
                              ) : (
                                product.locations.map((loc, idx) => (
                                  <div key={idx} className="flex items-center gap-1">
                                    {idx === 0 && <MapPin className="h-3 w-3 flex-shrink-0" />}
                                    {idx > 0 && <span className="w-3" />}
                                    {loc.locationName}: {loc.quantity}
                                  </div>
                                ))
                              )}
                            </div>
                          ) : product.location ? (
                            <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                              <MapPin className="h-3 w-3" />
                              {product.location}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="w-80 border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 flex flex-col">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Import Settings
            </h3>

            <div className="space-y-4 flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Default Category
                </label>
                <select
                  value={settings.default_category}
                  onChange={e => setSettings(prev => ({ ...prev, default_category: e.target.value }))}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="Shopify Import">Shopify Import</option>
                  {existingCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Used when product has no category
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Default Location
                </label>
                <select
                  value={settings.default_location}
                  onChange={e => setSettings(prev => ({ ...prev, default_location: e.target.value }))}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="">Use Shopify Location</option>
                  {existingLocations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Override location from Shopify
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Min Stock Level
                </label>
                <input
                  type="number"
                  min="0"
                  value={settings.default_min_stock}
                  onChange={e => setSettings(prev => ({ ...prev, default_min_stock: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Alert threshold for low stock
                </p>
              </div>

              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-green-700 dark:text-green-300">
                    <p className="font-medium mb-1">Price Import</p>
                    <p>Prices and cost prices will be imported from Shopify when available.</p>
                  </div>
                </div>
              </div>

              {products.some(p => p.sku_auto_generated) && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-blue-700 dark:text-blue-300">
                      <p className="font-medium mb-1">Auto-Generated SKUs</p>
                      <p>Products marked with <Zap className="inline h-3 w-3" /> have no SKU in Shopify. A unique SKU (SHOP-xxxxx) will be assigned automatically.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
              {importError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-xs text-red-700 dark:text-red-300">{importError}</span>
                </div>
              )}

              <div className="text-center text-sm text-gray-500 dark:text-gray-400 mb-2">
                <span className="font-semibold text-cyan-600 text-lg">{selectedSkus.size}</span>
                <span className="ml-1">products selected</span>
              </div>

              <Button
                onClick={handleImport}
                disabled={selectedSkus.size === 0 || importing}
                loading={importing}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
              >
                <Download className="h-4 w-4 mr-2" />
                Import {selectedSkus.size} Products
              </Button>

              <Button
                variant="secondary"
                onClick={onClose}
                disabled={importing}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
